import Controller from '@ember/controller';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { showModal } from 'sh-www/utils/modal-controls';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import { COMPANY_CREDIT } from 'sh-www/models/company-credit';
import { COMPANY_PROFILE_COMPONENT } from 'sh-www/components/company-profile/company-profile';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import { ADMIN_CREDIT_INVOICES_DEFAULT_INCLUDES } from 'sh-www/constants/routes/reports';

import {
  COMPANY_PROFILE__COMPANY_INCLUDES,
  COMPANY_PROFILE__COMPANY_INCLUDES_ADMIN,
  COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES,
  COMPANY_PROFILE__POSTINGS_INCLUDES,
  COMPANY_PROFILE__POSTINGS_TO_LOAD_COUNT,
} from 'sh-www/constants/routes/company-profile';

export default class CompanyProfileController extends Controller {
  queryParams = ['section'];

  @service router;
  @service('state-managers/application') applicationStateManager;
  @service('commands/assignment-commands') assignmentCommandService;
  @service('commands/company-commands') companyCommandService;
  @service('commands/company-contact-commands') companyContactService;
  @service('commands/company-team-commands') companyTeamCommandService;
  @service('commands/cost-code-commands') costCodeCommandsService;
  @service('error-tracker') errorTracker;
  @service('commands/note-commands') noteCommandService;
  @service session;
  @service shData;
  @service('commands/sh-general-invoice-commands') shGeneralInvoiceCommandService;
  @service store;

  section = null;
  isSavingEditedNoteMap = {};
  isEditingNoteMap = {};

  /**
   * Loads the company.
   *
   * @param {String} companySlug
   * @return {Ember.RSVP.Promise}
   */
  loadCompany(companySlug) {
    const { currentUser } = this.session;
    const baseIncludes = currentUser.isAdmin
      ? COMPANY_PROFILE__COMPANY_INCLUDES_ADMIN
      : COMPANY_PROFILE__COMPANY_INCLUDES;

    const companyIncludes = Object.assign([], baseIncludes);

    if (currentUser.superAdmin) {
      companyIncludes.push('subscription');
    }

    return this.companyCommandService
      .loadCompany(companySlug, companyIncludes);
  }

  /**
   * Cancels all in-flight tasks
   */
  cancelTasks() {
    this.reloadCompanyTask.cancelAll({ resetState: true });
    this.loadCompanyMembersTask.cancelAll({ resetState: true });
    this.loadCompanyCostCodeCreditsTask.cancelAll({ resetState: true });
    this.loadCreditPaidInvoicesTask.cancelAll({ resetState: true });
    this.loadCreditUnpaidInvoicesTask.cancelAll({ resetState: true });
    this.loadCreditDeletedInvoicesTask.cancelAll({ resetState: true });
    this.loadCompanyBilling.cancelAll({ resetState: true });
    this.loadCompanyNotes.cancelAll({ resetState: true });
    this.reloadCompanyTeamsTask.cancelAll({ resetState: true });
    this.reloadPostingsTask.cancelAll({ resetState: true });
    this.saveAdminCompanyNote.cancelAll({ resetState: true });
  }

  @dropTask *reloadCompanyTask() {
    const originalSlug = this.model.company.slug;

    const company = yield this.loadCompany(this.model.company.id);

    // redirect if slug changed because url relies on slug
    if (originalSlug !== company.slug) {
      this.router.replaceWith(
        EMBER_ROUTE_NAMES.COMPANY_PROFILE,
        company.slug,
        {
          queryParams: {
            section: COMPANY_PROFILE_COMPONENT.ADMIN_SECTIONS.ACCOUNT,
          },
        }
      );
      return;
    }

    this.set('model.company', company);
  }

  @dropTask
  *reloadCompanyTeamsTask(companyId) {
    try {
      const response = yield this.companyTeamCommandService.loadCompanyTeams(companyId);
      this.shData.pushResponseToStore(response);
      return this.shData.getModelArrayFromResponse(response, 'companyTeam');
    } catch (e) {
      this.errorTracker.error('Failed to reload company teams');
      this.applicationStateManager.addToast('Failed to reload teams');
    }
  }

  @dropTask
  *onSaveCompanyTeamTask(companyId, name, description, colorHex, companyTeamId = null) {
    try {
      if (companyTeamId) {
        return yield this.companyTeamCommandService.updateCompanyTeam({
          companyTeamId,
          name,
          description,
          colorHex,
        });
      }

      return yield this.companyTeamCommandService.storeCompanyTeam({
        companyId,
        name,
        description,
        colorHex,
      });
    } catch (e) {
      this.errorTracker.error('Failed to save company team');
      this.applicationStateManager.addToast('Failed to save team');
    }
  }

  @dropTask
  *onDeleteCompanyTeamTask(companyTeamId) {
    try {
      return yield this.companyTeamCommandService.destroyCompanyTeam(companyTeamId);
    } catch (e) {
      this.errorTracker.error('Failed to delete company team');
      this.applicationStateManager.addToast('Failed to delete team');
    }
  }

  /**
   * Loads the members of the company and invitees.
   *
   * @param {String} companyId
   * @return {Ember.RSVP.Promise}
   */
  @dropTask
  *loadCompanyMembersTask(companyId, includeTeams = false) {
    const { currentUser } = this.session;
    const isCompanyAdminOrHigher = currentUser.isAdmin || currentUser.isCompanyAdmin;

    const companyMemberIncludes = isCompanyAdminOrHigher
      ? ['all-employees.company-membership', 'invites']
      : ['approved-employees.company-membership'];

    if (includeTeams) {
      companyMemberIncludes.push('company-teams');

      if (isCompanyAdminOrHigher) {
        companyMemberIncludes.push(`all-employees.${COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES}`);
      } else {
        companyMemberIncludes.push(`approved-employees.${COMPANY_PROFILE__USER_TEAM_MEMBERSHIP_INCLUDES}`);
      }
    }

    return yield this.companyCommandService.loadCompany(companyId, companyMemberIncludes);
  }

  /**
   * Loads the postings.
   *
   * @param {String} companySlug
   * @return {Ember.RSVP.Promise}
   */
  loadPostings(companySlug) {
    const postingIncludes = [...COMPANY_PROFILE__POSTINGS_INCLUDES];

    if (this.session.currentUser.isSeller) {
      postingIncludes.push('applications');
      postingIncludes.push('assignment-user-meta');
    }

    return this.assignmentCommandService.loadAssignmentsDefault(
      ASSIGNMENT.STATUS.LIVE,
      1,
      COMPANY_PROFILE__POSTINGS_TO_LOAD_COUNT,
      undefined,
      postingIncludes,
      [],
      { company: companySlug, user_company_teams: this.session.currentUser.id },
    );
  }

  @dropTask *reloadPostingsTask() {
    const postings = yield this.loadPostings(this.model.company.slug);
    this.set('model.postings', postings);
  }

  @dropTask
  *saveAdminCompanyNote(company, noteContent) {
    try {
      yield this.noteCommandService.saveAdminNoteOnCompany(
        company,
        noteContent,
      );
      this.applicationStateManager.addToast('Saved note');
    } catch (e) {
      this.errorTracker.error('Failed too save note on company');
      this.applicationStateManager.addToast('Failed to save note');
      if (e.errors?.content) {
        this.applicationStateManager.addToast(e.errors.content);
      }
    }
  }

  /**
   * Loads the notes made on a company.
   *
   * @param {Company} company
   * @return {Ember.RSVP.Promise}
   */
  @dropTask
  *loadCompanyNotes(company) {
    try {
      return yield this.noteCommandService.loadAdminNotesOnCompany(
        company,
        'created-by-user',
      );
    } catch (e) {
      this.errorTracker.error(`loadCompanyNotes failed for company id ${company.id}`, this, e);
      this.applicationStateManager.addToast('Failed to load company notes');
    }
  }

  /**
   * Loads the a company's billing info.
   *
   * @param {Company} company
   * @return {Ember.RSVP.Promise}
   */
  @dropTask
  *loadCompanyBilling(company) {
    try {
      return yield this.companyCommandService.loadCompany(
        company.id,
        [
          'company-billing',
        ],
      );
    } catch (e) {
      this.errorTracker.error(`loadCompanyBilling failed for company id ${company.id}`, this, e);
      this.applicationStateManager.addToast('Failed to load company billing');
    }
  }

  /**
   * Loads the a company's billing contacts.
   *
   * @param {Company} company
   * @return {Ember.RSVP.Promise}
   */
  @dropTask
  *loadCompanyContacts(company) {
    try {
      const response = yield this.companyContactService.loadCompanyContacts(
        company.id,
        true,
        ['cost-codes:withTrashed'],
      );
      this.shData.pushResponseToStore(response);
      return this.shData.getModelArrayFromResponse(response, 'companyContact');
    } catch (e) {
      this.errorTracker.error(`loadCompanyContacts failed for company id ${company.id}`, this, e);
      this.applicationStateManager.addToast('Failed to load company billing contacts');
    }
  }

  /**
    * Loads default fees for sellers.
    *
    * @return {Ember.RSVP.Promise}
    */
  @dropTask
  *loadDefaultFees() {
    try {
      return yield this.companyCommandService.defaultFees();
    } catch (e) {
      this.errorTracker.error('loadDefaultFees failed', this, e);
      this.applicationStateManager.addToast('Failed to load default fees');
    }
  }

  /**
   * Updates the note with new content and update our state.
   *
   * @param {Company} company
   * @param {User} seller
   * @param {Note} note
   * @param {String} newNoteContent
   */
  @dropTask
  *onUpdateNote(note, newNoteContent) {
    const objectIsSavingKey = `isSavingEditedNoteMap.${note.id}`;

    this.set(objectIsSavingKey, true);

    try {
      yield this.noteCommandService.updateNote(
        note,
        newNoteContent
      );
      this.onToggleEditExistingNote(note.id, false);
    } catch (e) {
      this.errorTracker.error(`onUpdateNote failed note id ${note.id}`, this, e);
      this.applicationStateManager.addToast('Failed to update note');
    } finally {
      this.set(objectIsSavingKey, false);
    }
  }

  @dropTask
  *onDeleteNote(note) {
    try {
      return yield this.noteCommandService.deleteNote(note);
    } catch (e) {
      this.errorTracker.error(`onDeleteNote failed note id ${note.id}`, this, e);
      this.applicationStateManager.addToast('Failed to delete note');
    }
  }

  @dropTask
  *onDeleteCompanyContact(companyContact) {
    const { id } = companyContact;
    try {
      return yield this.companyContactService.destroyCompanyContact(
        id,
      );
    } catch (e) {
      this.errorTracker.error(`onDeleteCompanyContact failed for company contact id ${id}`, this, e);
      this.applicationStateManager.addToast('Failed to delete company contact');
    }
  }

  @dropTask
  *onUpdateCompanyContact(companyContact, newEmail) {
    const { id } = companyContact;
    try {
      return yield this.companyContactService.updateCompanyContact({
        companyContactId: id,
        email: newEmail,
      });
    } catch (e) {
      throw e;
    }
  }

  @dropTask
  *onUpdateCompanyBillingSettings(company, params) {
    try {
      const response = yield this.companyCommandService.updateBillingSettings(company, params);
      this.applicationStateManager.addToast('Update successful');
      return response;
    } catch (e) {
      throw e;
    }
  }


  @dropTask
  *loadCompanyCreditFactoidsTask() {
    try {
      return yield this.store.query('factoid', {
        type: COMPANY_CREDIT.FACTOID_TYPE,
        filter: {
          company_id: this.model.company.id,
        },
      });
    } catch (e) {
      this.errorTracker.error('Failed to load company credit factoids');
      this.applicationStateManager.addToast('Failed to load factoids');
    }
  }

  @dropTask
  *loadCompanyCostCodeCreditsTask() {
    const { company } = this.model;
    const costCodeIncludes = company.canUseCompanyTeams
        ? ['company-teams']
        : [];

    try {
      const costCodes = yield this.costCodeCommandsService.getCostCodesForCompany(
        company,
        costCodeIncludes,
      );

      return {
        costCodes,
        costCodesCurrentPage: 1,
      };
    } catch (error) {
      this.errorTracker.error('Failed to load company credits');
      this.applicationStateManager.addToast('Failed to load credits');
    }
  }

  @dropTask
  *loadCreditPaidInvoicesTask(page = 1) {
    const { currentUser } =  this.session;
    try {
      const response = yield this.shGeneralInvoiceCommandService.loadShGeneralInvoices({
        page,
        user_id: currentUser.id,
        includes: ADMIN_CREDIT_INVOICES_DEFAULT_INCLUDES,
        filters: {
          company_id: this.model.company.id,
          type: 'CompanyCredit',
          paid: true,
        }
      });
      this.shData.pushResponseToStore(response);
      const shGeneralPaidInvoices = this.shData.getModelArrayFromResponse(response, 'shGeneralInvoice');
      const shGeneralPaidInvoicesPagination = this.shData.getPaginationModelFromResponse(response);

      return {
        shGeneralPaidInvoices,
        shGeneralPaidInvoicesPagination,
      };
    } catch (error) {
      this.errorTracker.error('Failed to load paid credit invoices');
      this.applicationStateManager.addToast('Failed to load paid credit invoices');
    }
  }

  @dropTask
  *loadCreditUnpaidInvoicesTask(page = 1) {
    const { currentUser } =  this.session;
    try {
      const response = yield this.shGeneralInvoiceCommandService.loadShGeneralInvoices({
        page,
        user_id: currentUser.id,
        includes: ADMIN_CREDIT_INVOICES_DEFAULT_INCLUDES,
        filters: {
          company_id: this.model.company.id,
          type: 'CompanyCredit',
          paid: false,
        }
      });
      this.shData.pushResponseToStore(response);
      const shGeneralUnpaidInvoices = this.shData.getModelArrayFromResponse(response, 'shGeneralInvoice');
      const shGeneralUnpaidInvoicesPagination = this.shData.getPaginationModelFromResponse(response);

      return {
        shGeneralUnpaidInvoices,
        shGeneralUnpaidInvoicesPagination,
      };
    } catch (error) {
      this.errorTracker.error('Failed to load unpaid credit invoices');
      this.applicationStateManager.addToast('Failed to load unpaid credit invoices');
    }
  }

  @dropTask
  *loadCreditDeletedInvoicesTask(page = 1) {
    const { currentUser } =  this.session;
    try {
      const response = yield this.shGeneralInvoiceCommandService.loadShGeneralInvoices({
        page,
        user_id: currentUser.id,
        includes: ADMIN_CREDIT_INVOICES_DEFAULT_INCLUDES,
        filters: {
          company_id: this.model.company.id,
          type: 'CompanyCredit',
          only_trashed: currentUser.isAdmin,
        }
      });
      this.shData.pushResponseToStore(response);
      const shGeneralDeletedInvoices = this.shData.getModelArrayFromResponse(response, 'shGeneralInvoice');
      const shGeneralDeletedInvoicesPagination = this.shData.getPaginationModelFromResponse(response);

      return {
        shGeneralDeletedInvoices,
        shGeneralDeletedInvoicesPagination,
      };
    } catch (error) {
      this.errorTracker.error('Failed to load deleted credit invoices');
      this.applicationStateManager.addToast('Failed to load deleted credit invoices');
    }
  }

  @action
  onToggleEditExistingNote(noteId, isEditing) {
    // if edit clicked while another editor is open
    const editMapKeys = Object.keys(this.isEditingNoteMap);
    if (isEditing && this.isEditingNoteMap[editMapKeys[0]]) {
      showModal(
        this,
        'confirmation',
        {
          isShowCancelButton: true,
          titleText: 'Discard current message?',
          bodyText: 'Are you sure you wish to discard your changes?',
          actionButtonText: 'Confirm',
          confirmAction: () => {
            this.set('isEditingNoteMap', {
              [noteId]: isEditing,
            });
          }
        }
      );
    }

    this.set('isEditingNoteMap', {
      [noteId]: isEditing,
    });
  }

  /**
   * Called when the user navigates between tabs.
   *
   * @param {String} section
   */
  @action
  changeSection(section) {
    this.set('section', section);
  }
}
